import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Input from "../ui/Input";
import Button from "../ui/Button";
import ErrorMessage from "../ui/ErrorMessage";
import { registerAdmin } from "../../lib/apiClient";
import { ROUTES } from "../../lib/constants";
import { getErrorMessage } from "../../lib/utils";
import { PlatformSelection } from "../../types";

type Step = 1 | 2 | 3;

const PLATFORM_OPTIONS: Array<{
  key: PlatformSelection;
  label: string;
  description: string;
}> = [
  { key: "slack", label: "Slack", description: "Workspace access and channel automation" },
  { key: "discord", label: "Discord", description: "Role-based access inside guilds" },
  { key: "telegram", label: "Telegram", description: "Channel/group subscription access" },
];

export default function AdminRegistrationWizard() {
  const router = useNavigate();

  const [step, setStep] = useState<Step>(1);
  const [selectedPlatforms, setSelectedPlatforms] = useState<PlatformSelection[]>([]);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState<{
    email: string;
    loginId: string;
    simulated: boolean;
  } | null>(null);

  const progress = useMemo(() => (step / 3) * 100, [step]);

  const togglePlatform = (platform: PlatformSelection) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const validateStep = (targetStep: Step) => {
    if (targetStep === 1) {
      if (!selectedPlatforms.length) {
        setFormError("Select at least one platform to continue.");
        return false;
      }
    }
    if (targetStep === 2) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const usernameRegex = /^[a-zA-Z0-9._-]{3,30}$/;
      if (!emailRegex.test(email.trim())) {
        setFormError("Enter a valid email address.");
        return false;
      }
      if (!usernameRegex.test(username.trim())) {
        setFormError("Username must be 3-30 chars (letters, numbers, dot, underscore, hyphen).");
        return false;
      }
      if (password.trim().length < 8) {
        setFormError("Password must be at least 8 characters.");
        return false;
      }
    }
    setFormError(null);
    return true;
  };

  const nextStep = () => {
    if (!validateStep(step)) return;
    if (step < 3) setStep((prev) => (prev + 1) as Step);
  };

  const prevStep = () => {
    setFormError(null);
    if (step > 1) setStep((prev) => (prev - 1) as Step);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep(1) || !validateStep(2)) return;

    setIsLoading(true);
    const toastId = toast.loading("Creating your admin workspace...");
    try {
      const response = await registerAdmin({
        platforms: selectedPlatforms,
        email: email.trim(),
        username: username.trim(),
        password: password.trim(),
      });
      setSubmitted({
        email: response.admin.email,
        loginId: response.admin.loginId,
        simulated: response.emailStatus.simulated,
      });
      setFormError(null);
      toast.success("Registration complete. Check your inbox.", { id: toastId });
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      setFormError(message);
      toast.error(`Registration failed: ${message}`, { id: toastId });
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="w-full max-w-lg rounded-2xl ring-1 ring-dark-border/60 bg-dark-secondary/70 backdrop-blur-xl p-8 shadow-2xl space-y-5">
        <h2 className="text-2xl font-bold text-text-primary">Check your inbox</h2>
        <p className="text-text-secondary text-sm leading-relaxed">
          A welcome email has been prepared for{" "}
          <span className="text-text-primary font-semibold">{submitted.email}</span> with your login credentials.
        </p>
        <div className="rounded-lg border border-dark-border/60 bg-dark-primary/50 p-4">
          <p className="text-xs uppercase tracking-wider text-text-secondary mb-1">Login ID</p>
          <p className="text-text-primary font-semibold">{submitted.loginId}</p>
          {submitted.simulated && (
            <p className="text-xs text-functional-warning mt-2">
              Email sending is currently in placeholder mode.
            </p>
          )}
        </div>
        <p className="text-xs text-text-secondary leading-relaxed">
          After logging in, go to <span className="text-golden-accent font-medium">Settings → Integrations</span> to add your platform tokens and go live.
        </p>
        <div className="flex gap-3">
          <Button type="button" className="w-full" onClick={() => router(ROUTES.LOGIN_ADMIN)}>
            Go to Admin Login
          </Button>
          <Button type="button" variant="secondary" className="w-full" onClick={() => router(ROUTES.HOME)}>
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full max-w-xl rounded-2xl ring-1 ring-dark-border/60 bg-dark-secondary/70 backdrop-blur-xl p-8 shadow-2xl"
    >
      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs text-text-secondary mb-2 uppercase tracking-wider">
          <span>Step {step} of 3</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-2 rounded-full bg-dark-primary overflow-hidden">
          <div
            className="h-full rounded-full bg-golden-accent transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <h1 className="text-2xl md:text-3xl font-extrabold text-text-primary mb-2">Admin Registration</h1>
      <p className="text-text-secondary mb-6 text-sm">
        Create your workspace. You can configure platform tokens from Settings after login.
      </p>

      {/* Step 1 — Platform Selection */}
      {step === 1 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Step 1 — Platform Selection</h2>
          <p className="text-sm text-text-secondary">Choose one or more platforms for your subscriptions.</p>
          <div className="grid grid-cols-1 gap-3">
            {PLATFORM_OPTIONS.map((platform) => {
              const active = selectedPlatforms.includes(platform.key);
              return (
                <button
                  type="button"
                  key={platform.key}
                  onClick={() => togglePlatform(platform.key)}
                  className={`text-left rounded-xl border p-4 transition-all ${
                    active
                      ? "border-golden-accent bg-golden-accent/10"
                      : "border-dark-border/70 bg-dark-primary/40 hover:border-dark-border"
                  }`}
                >
                  <p className="text-text-primary font-semibold">{platform.label}</p>
                  <p className="text-xs text-text-secondary mt-1">{platform.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 2 — Account Configuration */}
      {step === 2 && (
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-text-primary">Step 2 — Account Configuration</h2>
          <p className="text-sm text-text-secondary mb-4">Set your primary admin login details.</p>
          <Input
            label="Email"
            id="email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@yourdomain.com"
            required
            disabled={isLoading}
          />
          <Input
            label="Username (Login ID)"
            id="username"
            name="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="admin_login"
            required
            disabled={isLoading}
          />
          <Input
            label="Password"
            id="password"
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Minimum 8 characters"
            required
            disabled={isLoading}
          />
        </div>
      )}

      {/* Step 3 — Review & Submit */}
      {step === 3 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Step 3 — Review & Submit</h2>
          <p className="text-sm text-text-secondary">Confirm your details and create your workspace.</p>
          <div className="rounded-xl border border-dark-border/70 bg-dark-primary/40 p-4 space-y-2 text-sm">
            <p className="text-text-secondary">
              Platforms:{" "}
              <span className="text-text-primary font-semibold uppercase">{selectedPlatforms.join(", ")}</span>
            </p>
            <p className="text-text-secondary">
              Email: <span className="text-text-primary font-semibold">{email}</span>
            </p>
            <p className="text-text-secondary">
              Username: <span className="text-text-primary font-semibold">{username}</span>
            </p>
          </div>
          <div className="rounded-xl border border-dark-border/30 bg-golden-accent/5 p-3 text-xs text-text-secondary">
            KYC and agreement signing are <span className="text-golden-accent">disabled by default</span>. SuperAdmin can enable them from the admin panel after setup.
          </div>
        </div>
      )}

      {formError && <ErrorMessage message={formError} className="mt-4" />}

      <div className="mt-8 flex items-center justify-between gap-3">
        <Button type="button" variant="secondary" onClick={prevStep} disabled={step === 1 || isLoading}>
          Back
        </Button>
        {step < 3 ? (
          <Button type="button" onClick={nextStep} disabled={isLoading}>
            Continue
          </Button>
        ) : (
          <Button type="submit" isLoading={isLoading} disabled={isLoading}>
            Complete Registration
          </Button>
        )}
      </div>

      <p className="mt-6 text-xs text-text-secondary text-center">
        Already have an account?{" "}
        <Link to={ROUTES.LOGIN_ADMIN} className="text-golden-accent hover:underline">
          Sign in
        </Link>
      </p>
    </form>
  );
}

